import { trigger } from '@angular/animations';
import {
  Component,
  EventEmitter,
  HostBinding,
  inject,
  InjectionToken,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import {
  AuthAdapterInterface,
  BaseFormComponent,
  LoginCredentials,
  StateEnum as State,
  UserType,
} from '@campus/public-portal';
// import '@diekeure/dcr-components/dcr-animated-kai.js';
import { ENVIRONMENT_LOGIN_TOKEN, EnvironmentLoginInterface } from '@campus/environment';
import '@diekeure/dcr-components/dcr-filled-button.js';
import '@diekeure/dcr-components/dcr-text-field.js';
import { Observable } from 'rxjs';
import { fadeInAnimation, slideInAnimation, wiggleAnimation } from '../../../animations';

// for toggling between login and password reset view
type Mode = 'login' | 'forgot-password';

@Component({
  standalone: false,
  selector: 'kabas-login-teacher',
  templateUrl: './login-teacher.component.html',
  styleUrls: ['./login-teacher.component.scss'],
  animations: [
    trigger('fadeIn', fadeInAnimation),
    trigger('slideIn', slideInAnimation),
    trigger('wiggle', wiggleAnimation),
  ],
})
export class LoginTeacherComponent
  extends BaseFormComponent<LoginCredentials, 'unknown_questi_teacher'>
  implements OnInit, OnChanges
{
  @Input() role: UserType;

  @Output() forgotPassword: EventEmitter<string> = new EventEmitter();
  @Output() ssoLoginClicked = new EventEmitter<string | InjectionToken<AuthAdapterInterface>>();

  @HostBinding('class') classList = 'kabas-login-teacher w-full';

  public mode: Mode = 'login'; // start in login
  public changes$: Observable<boolean>;

  // update texts based on the component's mode & state
  public uiState = {
    login: {
      error: 'Het opgegeven e-mailadres of gebruikersnaam en/of wachtwoord is niet correct.', // api error
      success: 'Je bent aangemeld. We sturen je zo dadelijk naar het platform.',
      unknown_questi_teacher:
        'Je Questi login kennen we nog niet. Log eerst in met je e-mail of gebruikersnaam en koppel dan je Questi account.',
      cta: 'Aanmelden',
      toggleMode: 'Wachtwoord vergeten?', // toggle between mode
    },
    'forgot-password': {
      error: 'Dit e-mailadres heeft geen account op Kabas.', // api error
      success:
        'Check je inbox: er is een e-mail gestuurd naar het opgegeven e-mailadres om je wachtwoord opnieuw in te stellen.',
      cta: 'Stuur een e-mail',
      toggleMode: 'Ik weet het weer',
    },
  };

  private fb = inject(UntypedFormBuilder);
  private loginEnvironment: EnvironmentLoginInterface = inject(ENVIRONMENT_LOGIN_TOKEN);
  public title = inject(Title);

  showQuestiLoginButton = this.loginEnvironment.questi;

  ngOnChanges(changes: SimpleChanges): void {
    if (this.mode === 'forgot-password' && changes['success']) {
      // Manually handle success state for forgot-password to avoid auto-redirect
      const success = changes['success'];
      if (!success.firstChange && success.currentValue !== null) {
        this.state = success.currentValue ? State.SUCCESS : State.ERROR;
        this.isErrorState = this.state === State.ERROR;
        this.setFormControlsState('enable');
      }
    } else {
      // Default behavior for other modes
      super.ngOnChanges(changes);
    }
  }

  ngOnInit() {
    this.title.setTitle((this.role === 'teacher' ? 'Leerkacht' : 'Schoolbeheerder') + ' aanmelden bij Kabas');
    this.buildForm();
    super.ngOnInit();
  }

  setMode(mode: Mode) {
    this.mode = mode;
    this.afterModeHook();
  }

  submit(adapter: string | InjectionToken<AuthAdapterInterface>) {
    if (this.mode === 'forgot-password') {
      this.sendEmail();
    } else {
      super.submit(adapter);
    }
  }

  public ssoLogin(adapter: string | InjectionToken<AuthAdapterInterface>) {
    this.ssoLoginClicked.emit(adapter);
  }

  private afterModeHook() {
    const passwordField = this.form.get('password');
    const emailField = this.form.get('email');
    if (this.mode === 'forgot-password') {
      passwordField.setValidators([]);
      emailField.setValidators([Validators.required, Validators.email]);
    } else {
      passwordField.setValidators([Validators.required]);
      emailField.setValidators([Validators.required]);
    }
    this.reset();
  }

  private buildForm() {
    this.form = this.fb.group({
      email: ['', Validators.required],
      password: ['', Validators.required],
    });
  }

  private sendEmail() {
    if (this.form.invalid) return;
    this.state = State.PROCESSING;
    this.forgotPassword.emit(this.form.get('email').value);
  }
}
